import uploadFileToS3, { formatFileName } from '@helpers/s3';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import { CroppedImage } from 'vl-common/src/components/CustomSurvey/customWidgets/ImageCrop';

const toBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

function isNumeric(str) {
  if (typeof str !== 'string') return false; // we only process strings!
  return (
    !Number.isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !Number.isNaN(parseFloat(str))
  ); // ...and ensure strings of whitespace fail
}

// Finds all questions in a survey definition with type region of interest
// Even if deeply nested
// Returns an array of with all the region of interest question names
const findRegionOfInterestQuestions = async (definition): Promise<string[]> => {
  const questionNames: string[] = [];
  await JSON.parse(JSON.stringify(definition), (key, value) => {
    if (isNumeric(key) && value?.type === 'region-of-interest') {
      questionNames.push(value?.name);
    }
    return value;
  });
  return questionNames;
};

const convertImageToCroppedJPEG = async (croppedImage: CroppedImage): Promise<string> => {
  return new Promise((resolve, reject) => {
    const { dataURL, crop } = croppedImage;

    const img = new Image();
    img.onload = () => {
      const croppedCanvas = document.createElement('canvas');

      // Get cropped image coordinates using provided percentages
      let cropX = crop.x;
      let cropY = crop.y;
      let cropWidth = crop.width;
      let cropHeight = crop.height;

      if (crop.unit === '%') {
        cropX = (crop.x / 100) * img.width;
        cropY = (crop.y / 100) * img.height;
        cropWidth = (crop.width / 100) * img.width;
        cropHeight = (crop.height / 100) * img.height;
      }

      // Set canvas to cropped image size
      croppedCanvas.width = cropWidth;
      croppedCanvas.height = cropHeight;

      const context = croppedCanvas.getContext('2d');
      // Draw cropped image
      context?.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

      // Resize canvas as 256x256
      const resizedCanvas = document.createElement('canvas');
      resizedCanvas.width = 256;
      resizedCanvas.height = 256;

      // Draw resized image
      const resizedContext = resizedCanvas.getContext('2d');
      resizedContext?.drawImage(croppedCanvas, 0, 0, cropWidth, cropHeight, 0, 0, 256, 256);

      // Convert to JPEG dataUrl
      const dataUrl = resizedCanvas.toDataURL('image/jpeg');

      resolve(dataUrl);
    };

    img.src = dataURL;
    img.onerror = (error) => reject(error);
  });
};

// For a specific question name, find all the responses
// There could be multiple responses per question name e.g for panel questions
// Returns an array of all the responses
const findAllResponsesForQuestionName = async (name: string, responses: any): Promise<any> => {
  const questionResponses: string[] = [];

  await JSON.parse(JSON.stringify(responses), (key, value) => {
    if (key === name) {
      questionResponses.push(value);
    }
    return value;
  });

  return questionResponses;
};

const findCroppedImages = async (definition, responses) => {
  const regionOfInterestQuestions = await findRegionOfInterestQuestions(definition);

  const allRegionOfInterestResponses = await Promise.all(
    regionOfInterestQuestions.flatMap(async (name) => {
      return findAllResponsesForQuestionName(name, responses);
    })
  );

  const flatResponses = allRegionOfInterestResponses.flat(2);
  return flatResponses;
};

export const convertDataUrlToFile = async (dataUrl: string, fileName: string): Promise<File> => {
  const res: Response = await fetch(dataUrl);
  const blob: Blob = await res.blob();
  return new File([blob], fileName, { type: 'image/jpeg' });
};

const createUrlForCaseFile = async (
  user_guid: string,
  fileName: string,
  case_id: number,
  dispatch: ReturnType<typeof useDispatch>
): Promise<any> =>
  dispatch(
    actions.uploadCaseDocsDatabase({
      user_guid,
      filename: fileName,
      case_id
    })
  );

export const findAndSaveCroppedImages = async (
  responses,
  definition,
  surveyObject,
  dispatch: ReturnType<typeof useDispatch>,
  userHasReadPermissions: boolean,
  case_id?: number
) => {
  // Find cropped images from survey definition
  const croppedImages = await findCroppedImages(definition, responses);

  if (!croppedImages) return;

  // If there are images with crop attributes, crop them and convert to JPEG
  const promisedImages = croppedImages.map(async (croppedImage) => {
    const dataUrl = await convertImageToCroppedJPEG(croppedImage);

    return { dataUrl, name: croppedImage.name };
  });

  const formattedImages = await Promise.all(promisedImages);

  // Convert base64 images into File objects
  const promisedImagesAsFiles = formattedImages.map((image) => convertDataUrlToFile(image.dataUrl, image.name));

  const formattedImagesAsFiles = await Promise.all(promisedImagesAsFiles);

  // if case ID provided we have to upload for specific case
  if (case_id) {
    const formattedFilesForUpload = formattedImagesAsFiles.map(async (file) => {
      const finalFilename = formatFileName(file, surveyObject.survey_id, true);
      // Create URL for S3 upload for file associated with case
      const urlResponse = await createUrlForCaseFile(surveyObject.patient_user_guid, finalFilename, case_id, dispatch);

      if (urlResponse.type === 'UPLOAD_DOCS_DATABASE_SUCCESS') {
        // Convert whole File into Base64
        const base64 = (await toBase64(file)).split(',')[1];
        // Upload file contents to S3
        await dispatch(
          actions.uploadDocs({
            user_guid: surveyObject.patient_user_guid,
            filename: finalFilename,
            base64
          })
        );
      }

      await Promise.all(formattedFilesForUpload);
    });
  } else {
    // Else no case ID then we just use upload file to S3 using helper function that handles all
    const promisedUploads = formattedImagesAsFiles?.map(async (file) => {
      const upload = await uploadFileToS3(
        file,
        dispatch,
        surveyObject.patient_user_guid,
        userHasReadPermissions,
        surveyObject.survey_id,
        '',
        true
      );
      return upload;
    });

    await Promise.all(promisedUploads);
  }
};
